import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Button,
  Grid,
  Divider,
  Box,
  CircularProgress,
  LinearProgress,
  Alert,
  Fade,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Transform as TransformIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { Deal, DealStatus } from '../types';
import { getDealById } from '../services/api';

interface DealCardProps {
  deal: Deal;
  selected: boolean;
  converting: boolean;
  onSelect: (dealId: string) => void;
  onViewDetails: (dealId: string) => void;
  onConvertToProject: (deal: Deal) => void;
  onDelete: (dealId: string) => void;
}

interface ProcessingStatus {
  stage: 'idle' | 'creating' | 'uploading' | 'processing' | 'scoring' | 'completed' | 'error';
  message: string;
  progress: number;
  documentsProcessed: number;
  totalDocuments: number;
  canClose: boolean;
}

const DealCard: React.FC<DealCardProps> = ({
  deal,
  selected,
  converting,
  onSelect,
  onViewDetails,
  onConvertToProject,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    stage: 'idle',
    message: '',
    progress: 0,
    documentsProcessed: 0,
    totalDocuments: 0,
    canClose: true,
  });

  const startPolling = React.useCallback(async (initialDealData: any) => {
    const maxAttempts = 60; // 5 minutes max (5 second intervals)
    let attempts = 0;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setProcessingStatus({
          stage: 'error',
          message: 'Processing timed out. May continue in background.',
          progress: 0,
          documentsProcessed: 0,
          totalDocuments: initialDealData.documents?.length || 0,
          canClose: true,
        });
        return;
      }

      try {
        const dealData = await getDealById(deal.id);
        const documents = dealData.documents || [];
        const totalDocs = documents.length;
        
        const processingDocs = documents.filter((doc: any) =>
          doc.processing_status === 'processing' || doc.processing_status === 'pending'
        );
        const completedDocs = documents.filter((doc: any) =>
          doc.processing_status === 'completed'
        );
        const failedDocs = documents.filter((doc: any) =>
          doc.processing_status === 'failed'
        );

        // Update processing status based on document processing
        if (failedDocs.length > 0) {
          setProcessingStatus({
            stage: 'error',
            message: `Document processing failed for ${failedDocs.length} document(s)`,
            progress: 0,
            documentsProcessed: completedDocs.length,
            totalDocuments: totalDocs,
            canClose: true,
          });
          return;
        }

        if (processingDocs.length > 0) {
          // Documents still processing
          const progress = totalDocs > 0 ? (completedDocs.length / totalDocs) * 60 : 30;
          setProcessingStatus({
            stage: 'processing',
            message: `Processing documents... ${completedDocs.length}/${totalDocs} complete`,
            progress,
            documentsProcessed: completedDocs.length,
            totalDocuments: totalDocs,
            canClose: false,
          });
        } else if (dealData.scoring_completed === 'processing' || dealData.scoring_completed === 'pending') {
          // Documents done, scoring in progress
          setProcessingStatus({
            stage: 'scoring',
            message: 'Scoring deal against investment criteria...',
            progress: 80,
            documentsProcessed: completedDocs.length,
            totalDocuments: totalDocs,
            canClose: false,
          });
        } else if (dealData.scoring_completed === 'completed') {
          // All done
          setProcessingStatus({
            stage: 'completed',
            message: dealData.score ?
              `Deal scored: ${dealData.score.overall_score}/10 (${dealData.score.recommendation})` :
              'Processing completed successfully!',
            progress: 100,
            documentsProcessed: completedDocs.length,
            totalDocuments: totalDocs,
            canClose: true,
          });
          return;
        } else if (dealData.scoring_completed === 'failed') {
          setProcessingStatus({
            stage: 'error',
            message: 'Deal scoring failed. Please try again.',
            progress: 0,
            documentsProcessed: completedDocs.length,
            totalDocuments: totalDocs,
            canClose: true,
          });
          return;
        }

        attempts++;
        setTimeout(poll, 5000); // Poll every 5 seconds
      } catch (error) {
        console.error('Error polling deal status:', error);
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  }, [deal.id, setProcessingStatus]);

  // Check if deal is in processing state and start polling
  useEffect(() => {
    const checkProcessingStatus = async () => {
      try {
        const dealData = await getDealById(deal.id);
        
        // Check if deal has documents being processed or scoring in progress
        const hasProcessingDocuments = dealData.documents?.some((doc: any) =>
          doc.processing_status === 'processing' || doc.processing_status === 'pending'
        );
        
        const isScoringInProgress = dealData.scoring_completed === 'processing' || dealData.scoring_completed === 'pending';
        
        if (hasProcessingDocuments || isScoringInProgress) {
          startPolling(dealData);
        } else if (dealData.scoring_completed === 'completed') {
          setProcessingStatus({
            stage: 'completed',
            message: dealData.score ?
              `Deal scored: ${dealData.score.overall_score}/10 (${dealData.score.recommendation})` :
              'Processing completed successfully!',
            progress: 100,
            documentsProcessed: dealData.documents?.length || 0,
            totalDocuments: dealData.documents?.length || 0,
            canClose: true,
          });
        } else if (dealData.scoring_completed === 'failed') {
          setProcessingStatus({
            stage: 'error',
            message: 'Processing failed. Please try again.',
            progress: 0,
            documentsProcessed: 0,
            totalDocuments: dealData.documents?.length || 0,
            canClose: true,
          });
        }
      } catch (error) {
        console.error('Error checking deal processing status:', error);
      }
    };

    // Only check if deal was recently created (within last 10 minutes)
    const dealCreatedAt = new Date(deal.created_at);
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    
    if (dealCreatedAt > tenMinutesAgo) {
      checkProcessingStatus();
    }
  }, [deal.id, deal.created_at, startPolling]);


  const getStatusIcon = () => {
    switch (processingStatus.stage) {
      case 'completed':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'processing':
      case 'scoring':
      case 'uploading':
        return <CircularProgress size={16} />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (processingStatus.stage) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'processing':
      case 'scoring':
      case 'uploading':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        cursor: 'pointer',
        border: selected ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.12)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      onClick={() => onSelect(deal.id)}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {deal.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {processingStatus.stage !== 'idle' && getStatusIcon()}
            <Chip label={deal.status} color="primary" size="small" />
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {deal.company || 'N/A'}
        </Typography>

        {/* Processing Status Alert */}
        {processingStatus.stage !== 'idle' && (
          <Fade in>
            <Alert 
              severity={getStatusColor() as any}
              sx={{ mb: 2, fontSize: '0.75rem' }}
              icon={false}
            >
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                  {processingStatus.message}
                </Typography>
                {processingStatus.stage !== 'completed' && processingStatus.stage !== 'error' && (
                  <LinearProgress 
                    variant="determinate" 
                    value={processingStatus.progress} 
                    sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                  />
                )}
                {processingStatus.totalDocuments > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Documents: {processingStatus.documentsProcessed}/{processingStatus.totalDocuments}
                  </Typography>
                )}
              </Box>
            </Alert>
          </Fade>
        )}

        <Divider sx={{ my: 1 }} />
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Industry</Typography>
            <Typography variant="body2">{deal.industry || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Value</Typography>
            <Typography variant="body2">
              {deal.estimatedValue ? `${deal.currency} ${deal.estimatedValue / 1000000}M` : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Stage</Typography>
            <Typography variant="body2">{deal.industry || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Source</Typography>
            <Typography variant="body2">{deal.source || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Date Added</Typography>
            <Typography variant="body2">{new Date(deal.created_at).toLocaleDateString()}</Typography>
          </Grid>
        </Grid>
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<BusinessIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(deal.id);
          }}
        >
          View Details
        </Button>
        {deal.status === DealStatus.ConvertedToProject ? (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<BusinessIcon />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${deal.project_id}`);
            }}
          >
            View Project Details
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={converting ? <CircularProgress size={16} /> : <TransformIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onConvertToProject(deal);
            }}
            disabled={converting}
          >
            {converting ? 'Converting...' : 'Convert to Project'}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(deal.id);
          }}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
};

export default DealCard;
